import { alertManager } from ".";
import { closeModal } from "../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { AppDispatch } from "../../ReduxToolkit/Store";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const getRecord = (
  value: unknown,
  key: string,
): UnknownRecord | undefined =>
  isRecord(value) && isRecord(value[key])
    ? (value[key] as UnknownRecord)
    : undefined;

const getString = (value: unknown, key: string) =>
  isRecord(value) && typeof value[key] === "string"
    ? (value[key] as string)
    : undefined;

const getValidationMessages = (value: unknown): string[] => {
  if (!isRecord(value)) return [];

  const errors = isRecord(value.errors) ? value.errors : undefined;
  if (!errors) return [];

  return Object.values(errors).flatMap((error) => {
    if (typeof error === "string") return [error];
    if (Array.isArray(error)) {
      return error.filter(
        (message): message is string => typeof message === "string",
      );
    }
    return [];
  });
};

export const getApiValidationErrors = (
  response: unknown,
): Record<string, string> => {
  const error = getRecord(response, "error") ?? response;
  const errorData =
    getRecord(error, "data") ?? (isRecord(error) ? error : undefined);
  if (!errorData) return {};

  const errors = isRecord(errorData.errors) ? errorData.errors : undefined;

  if (!errors) return {};

  return Object.entries(errors).reduce<Record<string, string>>(
    (fieldErrors, [field, value]) => {
      const message =
        typeof value === "string"
          ? value
          : Array.isArray(value)
            ? value.find((item): item is string => typeof item === "string")
            : undefined;

      if (message) fieldErrors[field] = message;
      return fieldErrors;
    },
    {},
  );
};

export const getApiResponseMessage = (response: unknown) => {
  const data = getRecord(response, "data");
  const error = getRecord(response, "error") ?? response;
  const errorData = getRecord(error, "data") ?? error;
  const validationMessages = getValidationMessages(errorData);

  return (
    validationMessages.join(" ") ||
    getString(data, "message") ||
    getString(errorData, "message") ||
    getString(error, "message") ||
    getString(response, "message") ||
    "An unexpected error occurred."
  );
};

export const isApiResponseSuccessful = (response: unknown) => {
  const data = getRecord(response, "data");
  return data?.success === true || Boolean(data?.status);
};

export const showApiResultAlert = (
  res: unknown,
  options: {
    title: string;
    description?: string;
    source?: string;
  },
  onConfirm?: () => void,
  dispatch?: AppDispatch,
  isCloseModal: boolean = false,
  isCloseDialog: boolean = false
) => {
  const { title, source, description } = options;
  const isSuccess = isApiResponseSuccessful(res);

  const isClose =
    (isSuccess && isCloseModal && dispatch) ||
    (isCloseDialog && dispatch);

  if (isClose) {
    dispatch(closeModal());
  }

  alertManager.showAlert(
    {
      type: isSuccess ? "success" : "warning",
      title,
      message: description ?? getApiResponseMessage(res),
      source,
    },
    onConfirm
  );
};
